import React, { useEffect, useState, useCallback } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import BlockIcon from "@material-ui/icons/Block";
import SearchIcon from "@material-ui/icons/Search";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { InputAdornment } from "@material-ui/core";
import { IonAlert } from "@ionic/react";
import AsignacionesV2Service from "../services/asignacionesV2";
import ModalAgregarDetalle from "./ModalAgregarDetalle";
import ModalMejorOperario from "./ModalMejorOperario";

interface Props {
  psId: string | number;
  onChanged?: () => void;
}

const headStyle: React.CSSProperties = { background: "#910434", color: "whitesmoke", fontWeight: "bold" };

const DIAS: { label: string; ent: string; sal: string }[] = [
  { label: "Lun", ent: "lun_ent", sal: "lun_sal" },
  { label: "Mar", ent: "mar_ent", sal: "mar_sal" },
  { label: "Mié", ent: "mie_ent", sal: "mie_sal" },
  { label: "Jue", ent: "jue_ent", sal: "jue_sal" },
  { label: "Vie", ent: "vie_ent", sal: "vie_sal" },
  { label: "Sáb", ent: "sab_ent", sal: "sab_sal" },
  { label: "Dom", ent: "dom_ent", sal: "dom_sal" },
];

const hhmm = (v: any) => (v ? String(v).slice(0, 5) : null);

const ListaAsignaciones: React.FC<Props> = ({ psId, onChanged }) => {
  const [operarios, setOperarios] = useState<any[]>([]);
  const [expandedOp, setExpandedOp] = useState<number | null>(null);
  const [cortesByOp, setCortesByOp] = useState<Record<number, any[]>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [filtroOp, setFiltroOp] = useState("");

  // Estado del cierre de un detalle de asignacion.
  const [cierre, setCierre] = useState<{ id: number | null; fecha: string }>({ id: null, fecha: "" });
  const [alertMsg, setAlertMsg] = useState("");

  // Confirmacion del cambio de supervisor (aplica a todas las asignaciones abiertas del operario).
  const [supConfirm, setSupConfirm] = useState<{ opId: number; corte: any; nuevo: boolean } | null>(null);

  // Estado del modal "buscar mejor operario" (analisis + reemplazo).
  const [mejorOp, setMejorOp] = useState<{ corte: any; op: any } | null>(null);

  const cargarOperarios = useCallback(async () => {
    if (!psId) return;
    const datos = await AsignacionesV2Service.operarios(psId);
    setOperarios(datos);
  }, [psId]);

  useEffect(() => {
    cargarOperarios();
  }, [cargarOperarios]);

  const toggleExpand = async (op: any) => {
    if (expandedOp === op.id) {
      setExpandedOp(null);
      return;
    }
    setExpandedOp(op.id);
    if (!cortesByOp[op.id]) {
      const cortes = await AsignacionesV2Service.cortesOperario(psId, op.id);
      setCortesByOp((prev) => ({ ...prev, [op.id]: cortes }));
    }
  };

  const confirmarCierre = async () => {
    if (!cierre.id || !cierre.fecha) return;
    try {
      const resp = await AsignacionesV2Service.cerrar(cierre.id, cierre.fecha);
      setAlertMsg(resp?.data?.mensaje || "Asignación cerrada");
    } catch (error: any) {
      setAlertMsg(error?.response?.data?.error || "No se pudo cerrar la asignación");
    }
    setCierre({ id: null, fecha: "" });
    // Refrescamos cortes del operario abierto y los totales del PS.
    if (expandedOp != null) {
      const cortes = await AsignacionesV2Service.cortesOperario(psId, expandedOp);
      setCortesByOp((prev) => ({ ...prev, [expandedOp]: cortes }));
    }
    await cargarOperarios();
    if (onChanged) onChanged();
  };

  const onGuardado = async () => {
    setModalOpen(false);
    await cargarOperarios();
    if (onChanged) onChanged();
  };

  const esSupervisor = (c: any) => c.supervisor === 1 || c.supervisor === true || c.supervisor === "1";

  // Al tocar el switch pedimos confirmacion antes de aplicar (el cambio impacta a todas
  // las asignaciones abiertas del operario en este punto de servicio).
  const pedirCambioSupervisor = (opId: number, corte: any) => {
    setSupConfirm({ opId, corte, nuevo: !esSupervisor(corte) });
  };

  // Aplica el cambio de supervisor (auditado en el backend) y refresca.
  const aplicarCambioSupervisor = async () => {
    if (!supConfirm) return;
    const { opId, corte, nuevo } = supConfirm;
    setSupConfirm(null);
    try {
      const resp = await AsignacionesV2Service.editarDetalle(corte.id, { supervisor: nuevo });
      setAlertMsg(resp?.data?.mensaje || "Asignación actualizada");
    } catch (error: any) {
      setAlertMsg(error?.response?.data?.error || "No se pudo actualizar el supervisor");
    }
    const cortes = await AsignacionesV2Service.cortesOperario(psId, opId);
    setCortesByOp((prev) => ({ ...prev, [opId]: cortes }));
    if (onChanged) onChanged();
  };

  // Tras un reemplazo (cerrar actual + crear nueva) refrescamos cortes y totales.
  const onReemplazado = async () => {
    const ctx = mejorOp;
    setMejorOp(null);
    setAlertMsg("Reemplazo realizado correctamente.");
    if (ctx?.op?.id != null) {
      const cortes = await AsignacionesV2Service.cortesOperario(psId, ctx.op.id);
      setCortesByOp((prev) => ({ ...prev, [ctx.op.id]: cortes }));
    }
    await cargarOperarios();
    if (onChanged) onChanged();
  };

  const operariosFiltrados = operarios.filter((op) =>
    `${op.operario || ""} ${op.legajo || ""}`.toLowerCase().includes(filtroOp.toLowerCase())
  );

  return (
    <Paper style={{ width: "100%" }}>
      <IonAlert isOpen={!!alertMsg} onDidDismiss={() => setAlertMsg("")} message={alertMsg} buttons={["Cerrar"]} />

      <Box display="flex" alignItems="center" justifyContent="space-between" px={1} py={1}>
        <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#910434" }}>
          Asignaciones
        </Typography>
        <Button
          variant="contained"
          onClick={() => setModalOpen(true)}
          style={{ background: "#910434", color: "white" }}
        >
          + Agregar Detalle
        </Button>
      </Box>

      <Box px={1} pb={1}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Buscar operario asignado (nombre o legajo)"
          value={filtroOp}
          onChange={(e) => setFiltroOp(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Table size="small" style={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...headStyle, width: 44 }} />
            <TableCell style={headStyle}>Operario</TableCell>
            <TableCell style={{ ...headStyle, width: 110 }}>Legajo</TableCell>
            <TableCell style={{ ...headStyle, width: 90 }} align="center">Total Hrs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {operariosFiltrados.map((op) => (
            <React.Fragment key={op.id}>
              <TableRow hover>
                <TableCell>
                  <IconButton size="small" onClick={() => toggleExpand(op)}>
                    {expandedOp === op.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{op.operario}</TableCell>
                <TableCell>{op.legajo}</TableCell>
                <TableCell align="center">{op.total_asignado}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                  <Collapse in={expandedOp === op.id} timeout="auto" unmountOnExit>
                    <Box margin={1}>
                      {(cortesByOp[op.id] || []).length === 0 ? (
                        <Typography variant="body2">Sin cortes registrados</Typography>
                      ) : (
                        <TableContainer style={{ overflowX: "auto" }}>
                        <Table size="small" style={{ minWidth: 760 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell style={headStyle}>Fecha inicio</TableCell>
                              {DIAS.map((d) => (
                                <TableCell key={d.label} align="center" style={headStyle}>{d.label}</TableCell>
                              ))}
                              <TableCell align="center" style={headStyle}>Total</TableCell>
                              <TableCell align="center" style={headStyle}>Supervisor</TableCell>
                              <TableCell align="center" style={headStyle}>Acción</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(cortesByOp[op.id] || []).map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>{c.fecha_inicio}</TableCell>
                                {DIAS.map((d) => {
                                  const ent = hhmm(c[d.ent]);
                                  const sal = hhmm(c[d.sal]);
                                  return (
                                    <TableCell key={d.label} align="center" style={{ fontSize: 11 }}>
                                      {ent && sal ? `${ent}-${sal}` : "—"}
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="center">{c.total_horas}</TableCell>
                                <TableCell align="center">
                                  <Switch
                                    size="small"
                                    color="primary"
                                    checked={esSupervisor(c)}
                                    onChange={() => pedirCambioSupervisor(op.id, c)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    title="Buscar mejor operario"
                                    onClick={() => setMejorOp({ corte: c, op })}
                                  >
                                    <SwapHorizIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Cerrar asignación"
                                    onClick={() => setCierre({ id: c.id, fecha: "" })}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
          {operariosFiltrados.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">Sin operarios asignados</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Dialogo de cierre de asignacion */}
      <Dialog open={cierre.id != null} onClose={() => setCierre({ id: null, fecha: "" })}>
        <DialogTitle>Cerrar asignación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Indique la fecha de fin de la asignación.
          </Typography>
          <TextField
            type="date"
            value={cierre.fecha}
            onChange={(e) => setCierre((prev) => ({ ...prev, fecha: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCierre({ id: null, fecha: "" })}>Cancelar</Button>
          <Button onClick={confirmarCierre} disabled={!cierre.fecha} style={{ color: "#910434" }}>
            Cerrar asignación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmacion del cambio de supervisor */}
      <Dialog open={supConfirm != null} onClose={() => setSupConfirm(null)}>
        <DialogTitle>{supConfirm?.nuevo ? "Marcar como supervisor" : "Quitar supervisor"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se {supConfirm?.nuevo ? "marcará" : "quitará"} como supervisor a{" "}
            <strong>{supConfirm?.corte?.operario || "este operario"}</strong> en{" "}
            <strong>todas sus asignaciones abiertas de este punto de servicio</strong>. ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupConfirm(null)}>Cancelar</Button>
          <Button onClick={aplicarCambioSupervisor} style={{ color: "#910434" }}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <ModalAgregarDetalle
        open={modalOpen}
        psId={psId}
        onClose={() => setModalOpen(false)}
        onGuardado={onGuardado}
      />

      <ModalMejorOperario
        open={mejorOp != null}
        psId={psId}
        corte={mejorOp?.corte || null}
        operario={mejorOp?.op || null}
        onClose={() => setMejorOp(null)}
        onReemplazado={onReemplazado}
      />
    </Paper>
  );
};

export default ListaAsignaciones;
